/**
 * HeroCards — las tres tarjetas-hero del Home v2 (handoff
 * design_handoff_home_v2): Conversar (lima) · Escuchar (violeta) · Programa
 * (coral). Cards neutras (gris de la app): el acento vive sólo en badge, CTA,
 * arte y botones internos. El arte de la esquina son los SVG ilustrados del
 * handoff en `public/home/*.svg` (escuchar recoloreado de azul a --purple).
 *
 * Las tres comparten el alto sobrante con reparto PONDERADO (~40% Conversar,
 * ~30% cada una de las otras — flex 4/3/3 en home-v2.css): Conversar es el
 * core y pesa un poco más, sin dejar huecos gigantes en ninguna.
 *
 * Conversar: CTA FIJO "Empezar conversación"; los chips de escenario navegan
 * DIRECTO a /scenarios filtrado (un tap = una acción — antes elegían un
 * estado local que mutaba el CTA, un patrón de dos pasos escondido en la
 * acción más importante de la app).
 *
 * Datos: las filas "siguiendo" / "unidad en curso" reusan `useHomeResume()`
 * (ResumeHero) — la card degrada sin fila cuando no hay nada que retomar.
 */
import { useState, type ComponentType } from 'react';
import { useGlobalListenBarActive } from '../../listen/GlobalListenBar.js';
import { useListen } from '../../listen/ListenProvider.js';
import { coverImageUrl } from '../../listen/coverMeta.js';
import { fmtClock } from '../../listen/listenUi.js';
import { IcTravel, IcWork } from '../../scenarios/icons.js';
import {
  IconArrowRight,
  IconBook,
  IconChevronRight,
  IconHome,
  IconMic,
  IconPause,
  IconPlay,
} from '../icons.js';
import type { HomeResume, ResumeListen, ResumeUnit } from './ResumeHero.js';

const PURPLE = '#a78bfa';
const CORAL = '#ff9d6c';

// ─── Tarjeta 1 · Conversar ──────────────────────────────────────────────────

interface ScenarioChip {
  id: string;
  label: string;
  /** Categoría real del catálogo de escenarios (deep-link al tab del hub). */
  category: string;
  icon: ComponentType<{ size?: number }>;
}

// Tiles = las 3 categorías reales del catálogo (work/travel/daily), con los
// mismos íconos que usa el hub de escenarios para sus tabs. La conversación
// libre ya tiene su CTA arriba — no se duplica como tile.
const SCENARIO_CHIPS: readonly ScenarioChip[] = [
  { id: 'work', label: 'Trabajo', category: 'work', icon: IcWork },
  { id: 'travel', label: 'Viaje', category: 'travel', icon: IcTravel },
  { id: 'daily', label: 'Día a día', category: 'daily', icon: IconHome },
];

export function ConverseHeroCard({
  onStart,
  onScenarios,
}: {
  onStart: () => void;
  /** Abre el hub de escenarios, opcionalmente filtrado por categoría. */
  onScenarios: (category?: string) => void;
}) {
  return (
    <div className="h2-card h2-conv">
      <img className="h2-art" src="/home/conversar.svg" alt="" aria-hidden="true" />
      <span className="h2-badge" style={{ background: 'rgba(212,242,62,0.16)', color: 'var(--lime)' }}>
        Conversar
      </span>
      <div className="h2-title">Hablemos ahora</div>
      <p className="h2-desc">Conversá con un coach que se adapta a vos.</p>
      <button type="button" className="h2-cta" onClick={onStart}>
        <IconMic size={17} />
        Empezar conversación
      </button>
      <div className="h2-divider" />
      <div className="h2-sublabel">O elegí un escenario</div>
      <div className="h2-chips-wrap">
        <div className="h2-chiprow">
          {SCENARIO_CHIPS.map((ch) => {
            const Icon = ch.icon;
            return (
              <button
                key={ch.id}
                type="button"
                className="h2-chip"
                onClick={() => onScenarios(ch.category)}
              >
                <span style={{ color: 'var(--lime)', display: 'flex' }}>
                  <Icon size={18} />
                </span>
                <span>{ch.label}</span>
              </button>
            );
          })}
        </div>
        <button type="button" className="h2-chip h2-chip-more" onClick={() => onScenarios()}>
          <span className="h2-chip-moreic">
            <IconChevronRight size={14} />
          </span>
          <span>Ver todos</span>
        </button>
      </div>
    </div>
  );
}

// ─── Tarjeta 2 · Escuchar ───────────────────────────────────────────────────

function bookInitials(title: string): string {
  return title
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase();
}

/** Ecualizador "sonando" (mismo patrón que .ch-row-eq de la Audioteca).
 *  Vive en el tag "Siguiendo" (reemplaza al dot), no sobre el cover. */
function PlayingEq() {
  return (
    <span className="h2-eq-bars" aria-hidden="true">
      <i />
      <i />
      <i />
    </span>
  );
}

/** Miniatura del libro en la fila "siguiendo".
 *
 *  La cover ILUSTRADA vive en el front (`public/covers/<slug>.webp`, ver
 *  `coverImageUrl`), NO en `cover_url` del catálogo — esa columna está vacía
 *  para todos los libros. Mirando sólo `book.coverUrl` esta miniatura caía
 *  SIEMPRE a las iniciales mientras la Audioteca y el mini-player sí pintaban
 *  el arte. Orden: backend > cover del front > cover generativa (iniciales).
 */
function ListenCover({ book }: { book: ResumeListen['book'] }) {
  const url = book.coverUrl ?? coverImageUrl(book.slug);
  // Si la imagen falla en cargar caemos a las iniciales (mismo patrón que BookCover).
  const [imgOk, setImgOk] = useState(true);
  const accent = book.accent ?? PURPLE;
  if (url && imgOk) {
    return (
      <span className="h2-cover">
        <img
          className="h2-cover-img"
          src={url}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setImgOk(false)}
        />
      </span>
    );
  }
  return (
    <span
      className="h2-cover"
      style={{ background: `linear-gradient(150deg, ${accent} 0%, ${accent}88 100%)` }}
    >
      <span className="script">{bookInitials(book.title)}</span>
    </span>
  );
}

export function ListenHeroCard({
  resume,
  onOpenPlayer,
  onExplore,
}: {
  resume: HomeResume;
  onOpenPlayer: () => void;
  onExplore: () => void;
}) {
  const L = useListen();
  const listen = resume.listen;
  const suggested = resume.suggestedListen;
  // Con la barra flotante global en pantalla, el reproductor del Home ES esa
  // barra (lima, sobre el tab bar, igual que en el resto de la app): esta fila
  // se corre para no ofrecer el mismo control dos veces en la misma pantalla.
  // Se corre por CSS y no desmontándose por DOS razones: la barra es
  // móvil/tablet (en escritorio no existe y la fila sigue siendo el único
  // control), y el correrse está animado — la fila se va hacia abajo, hacia
  // donde aparece la barra, mientras `.h2-inner-slot` cierra el hueco. Ese
  // relevo necesita que el nodo siga ahí para animarlo. Ver home-v2.css.
  const barActive = useGlobalListenBarActive();

  // Play/pausa in-situ (no navega): si está cargado en sesión togglea el
  // <audio> único; si vino del backend, lo arranca (mismo patrón que ResumeHero).
  const togglePlay = (): void => {
    if (!listen) return;
    if (listen.live) L.toggle();
    else resume.startListen();
  };

  // Estado vacío rico: arranca el primer capítulo del libro sugerido y abre el
  // player (mismo patrón open-async + navigate inmediato que openPlayer).
  const openSuggested = (): void => {
    if (!suggested) return;
    void L.openChapter(suggested.book, suggested.chapter, suggested.edition, { play: true });
    onOpenPlayer();
  };

  return (
    <div className="h2-card h2-listen">
      <img className="h2-art" src="/home/escuchar.svg" alt="" aria-hidden="true" />
      <span className="h2-badge" style={{ background: 'rgba(167,139,250,0.16)', color: PURPLE }}>
        Escuchar
      </span>
      <div className="h2-title">Escuchá y aprendé</div>
      <p className="h2-desc">Historias reales, entrevistas y más.</p>
      {listen ? (
        <div className={`h2-inner-slot${barActive ? ' is-in-global-bar' : ''}`}>
          {/* El clip existe para que el hueco pueda cerrar hasta 0: la fila, con
              su padding y su borde, no puede. Ver home-v2.css. */}
          <div className="h2-inner-clip">
            <div className="h2-inner" onClick={onOpenPlayer} role="button" tabIndex={0}>
              <ListenCover book={listen.book} />
              <div className="h2-inner-info">
                <span className="h2-inner-tag" style={{ color: PURPLE }}>
                  {listen.playing ? <PlayingEq /> : <span className="dot" style={{ background: PURPLE }} />}
                  {listen.playing ? 'Sonando' : 'Siguiendo'}
                </span>
                <div className="h2-inner-title">{listen.book.title}</div>
                <div className="h2-inner-meta">
                  Cap. {listen.chapter.order} · {fmtClock(listen.posSec)}
                  {listen.durationSec > 0 ? ` / ${fmtClock(listen.durationSec)}` : ''}
                </div>
              </div>
              <button
                type="button"
                className="h2-roundbtn"
                style={{ background: PURPLE, color: '#1d1438' }}
                onClick={(e) => {
                  e.stopPropagation();
                  togglePlay();
                }}
                aria-label={listen.playing ? 'Pausar' : 'Seguir escuchando'}
              >
                {listen.playing ? <IconPause size={15} /> : <IconPlay size={15} />}
              </button>
            </div>
          </div>
        </div>
      ) : suggested ? (
        <div className="h2-inner" onClick={openSuggested} role="button" tabIndex={0}>
          <ListenCover book={suggested.book} />
          <div className="h2-inner-info">
            <span className="h2-inner-tag" style={{ color: PURPLE }}>
              <span className="dot" style={{ background: PURPLE }} />
              Empezá por acá
            </span>
            <div className="h2-inner-title">{suggested.book.title}</div>
            <div className="h2-inner-meta">
              Cap. {suggested.chapter.order}
              {suggested.chapter.durationTargetMin ? ` · ${suggested.chapter.durationTargetMin} min` : ''}
            </div>
          </div>
          <button
            type="button"
            className="h2-roundbtn"
            style={{ background: PURPLE, color: '#1d1438' }}
            onClick={(e) => {
              e.stopPropagation();
              openSuggested();
            }}
            aria-label="Empezar a escuchar"
          >
            <IconPlay size={15} />
          </button>
        </div>
      ) : resume.listenError ? (
        // Falló la red al traer el progreso (≠ "no hay nada que retomar"): en vez
        // de caer al estado vacío silencioso, avisamos y ofrecemos reintento.
        <div
          className="h2-inner"
          onClick={() => resume.retryListen()}
          role="button"
          tabIndex={0}
          aria-label="No pudimos cargar tu progreso. Reintentar."
        >
          <div className="h2-inner-info">
            <span className="h2-inner-tag" style={{ color: PURPLE }}>
              <span className="dot" style={{ background: PURPLE }} />
              Sin conexión
            </span>
            <div className="h2-inner-title">No pudimos cargar tu progreso</div>
            <div className="h2-inner-meta">Tocá para reintentar</div>
          </div>
          <span
            className="h2-roundbtn"
            style={{ background: PURPLE, color: '#1d1438' }}
            aria-hidden="true"
          >
            ↻
          </span>
        </div>
      ) : null}
      <button type="button" className="h2-explore" style={{ color: PURPLE }} onClick={onExplore}>
        <span>Explorar contenido</span>
        <IconChevronRight size={16} />
      </button>
    </div>
  );
}

// ─── Tarjeta 3 · Programa ───────────────────────────────────────────────────

export function ProgramHeroCard({
  unit,
  onOpen,
  onExplore,
}: {
  unit: ResumeUnit | null;
  /** Fila de la unidad → /program con el popover de sub-unidades abierto. */
  onOpen: () => void;
  /** Footer "Ver todo el programa" → /program general (sin popover). */
  onExplore: () => void;
}) {
  const pct = unit && unit.total > 0 ? unit.mastered / unit.total : 0;
  return (
    <div className="h2-card h2-program">
      <img className="h2-art" src="/home/programa.svg" alt="" aria-hidden="true" />
      <span className="h2-badge" style={{ background: 'rgba(255,157,108,0.15)', color: CORAL }}>
        Programa
      </span>
      <div className="h2-title" style={{ marginBottom: 7 }}>
        Tu plan de estudio
      </div>
      <div className="h2-inner" onClick={onOpen} role="button" tabIndex={0}>
        <span className="h2-cover" style={{ background: 'rgba(255,157,108,0.16)', color: CORAL }}>
          <IconBook size={19} />
        </span>
        <div className="h2-inner-info">
          {unit ? (
            <>
              <div className="h2-inner-title">{unit.title}</div>
              <div className="h2-inner-meta">
                {unit.mastered} de {unit.total} subunidades
              </div>
              <div className="h2-inner-bar">
                <i style={{ width: `${Math.max(pct * 100, 4)}%`, background: CORAL }} />
              </div>
            </>
          ) : (
            <>
              <div className="h2-inner-title">Empezá tu plan</div>
              <div className="h2-inner-meta">Tu currículum: 33 capítulos, A1→C2</div>
            </>
          )}
        </div>
        <button
          type="button"
          className="h2-roundbtn"
          style={{ background: CORAL, color: '#2a1408' }}
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
          aria-label="Seguir el programa"
        >
          <IconArrowRight size={15} />
        </button>
      </div>
      <button type="button" className="h2-explore" style={{ color: CORAL }} onClick={onExplore}>
        <span>Ver todo el programa</span>
        <IconChevronRight size={16} />
      </button>
    </div>
  );
}
